// Me.jsx — страница «Мой аккаунт» админ-панели.
// Левая колонка — карточка профиля (аватар + уровень/алмазы/рейтинг/роль/подписка).
// Правая — «инфо-блок» с объяснением, что аккаунт единый на трёх сервисах
// (Минкрафтия / РобоТехника / СтартКласс) и какие возможности даёт роль.
// Снизу — стандартные Комментарии (как было).

import React, { useEffect, useMemo, useRef } from 'react';
import cl from './Me.module.css';
import useStore from '../../store/usersStore';
import { useAuth } from '../../context/AuthContext';
import ava from './img/ava.png';
import Comments from '../../components/UI/Comments/Comments';
import { FOLDER_PROFILE } from '../../API/API';
import {
    IconUser, IconShieldCheck, IconStudents, IconDiamond, IconStar,
    IconExternal, IconCheck, IconBook, IconGamepad, IconChart,
    IconEye, IconAlert,
} from '../../components/Icons';

// Описания ролей: что эта роль означает и что разрешает.
// Источник значений — AuthContext: 'owner' | 'teacher' | 'admin' | 'none'.
const ROLE_DESCRIPTIONS = {
    owner: {
        label: 'Руководитель центра',
        icon: <IconShieldCheck width={14} height={14}/>,
        tone: 'gold',
        summary: 'Владелец детского центра. Полный доступ ко всем разделам админ-панели.',
        abilities: [
            'Управление учениками, курсами, мероприятиями, платежами',
            'Подключение аккаунтов учеников к Майнкрафтии',
            'Назначение преподавателей и администраторов',
            'Проверка проектов РобоТехники и выдача наград',
        ],
    },
    admin: {
        label: 'Администратор',
        icon: <IconShieldCheck width={14} height={14}/>,
        tone: 'red',
        summary: 'Помощник руководителя с правами на изменение данных в админ-панели.',
        abilities: [
            'Редактирование учеников, курсов и мероприятий',
            'Работа с платежами и журналом посещаемости',
            'Проверка проектов и выдача наград',
        ],
    },
    teacher: {
        label: 'Преподаватель',
        icon: <IconUser width={14} height={14}/>,
        tone: 'green',
        summary: 'Доступ к журналу своих курсов и проверке проектов учеников.',
        abilities: [
            'Просмотр и редактирование журнала своих курсов',
            'Проверка проектов РобоТехники',
            'Просмотр прогресса учеников',
        ],
    },
    none: {
        label: 'Без доступа',
        icon: <IconEye width={14} height={14}/>,
        tone: 'gray',
        summary: 'Аккаунт обычного пользователя Майнкрафтии. Прав в админ-панели центра нет.',
        abilities: [
            'Заниматься на сайте Майнкрафтии и решать задачи',
            'Чтобы получить доступ к админ-панели — попросите руководителя центра '
              + 'назначить вам роль в разделе «Преподаватели и админы»',
        ],
    },
};

// Карточки трёх сервисов одной экосистемы.
const SERVICES = [
    {
        key: 'minecraftia',
        title: 'Майнкрафтия',
        url: 'https://minecraftia-school.ru',
        desc: 'Онлайн-школа программирования: курсы, задачи, рейтинг, алмазы.',
        icon: <IconGamepad width={20} height={20}/>,
        tone: 'green',
    },
    {
        key: 'robotehnick',
        title: 'РобоТехника',
        url: 'https://robotehnick.ru',
        desc: 'Курсы электроники и робототехники с проверкой проектов.',
        icon: <IconChart width={20} height={20}/>,
        tone: 'blue',
    },
    {
        key: 'startklass',
        title: 'СтартКласс',
        url: 'https://startklass.ru',
        desc: 'Платформа для владельцев детских центров и преподавателей.',
        icon: <IconBook width={20} height={20}/>,
        tone: 'gold',
    },
];

const Me = () => {
    const { user_profile, diamond, rating } = useStore();
    const { accessRole } = useAuth();
    const topRef = useRef(null);

    useEffect(() => {
        if (topRef.current) {
            topRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, []);

    const role = useMemo(
        () => ROLE_DESCRIPTIONS[accessRole] || ROLE_DESCRIPTIONS.none,
        [accessRole],
    );

    const userName = user_profile?.firstName || 'Пользователь';
    const userLevel = user_profile?.level || 0;

    return (
        <div ref={topRef} className={cl.page_wrapper}>
            <div className={cl.wrap_content_1}>

                {/* Шапка-карточка */}
                <div className={cl.pageHeaderV2}>
                    <div className={cl.pageHeaderLeft}>
                        <div className={cl.pageHeaderIcon}>
                            <IconUser width={22} height={22}/>
                        </div>
                        <div className={cl.pageHeaderText}>
                            <div className={cl.pageHeaderTopRow}>
                                <h1 className={cl.pageHeaderTitle}>Мой аккаунт</h1>
                                <span className={`${cl.rolePill} ${cl[`rolePill_${role.tone}`]}`}>
                                    {role.icon}
                                    <span>{role.label}</span>
                                </span>
                            </div>
                            <div className={cl.breadcrumbs}>
                                АдминПанель · Профиль · Личные данные и роль
                            </div>
                        </div>
                    </div>
                </div>

                <div className={cl.grid}>
                    {/* ===== Левая колонка — карточка профиля ===== */}
                    <aside className={cl.profileCard}>
                        <div className={cl.avatarWrapper}>
                            <img
                                className={cl.avatar}
                                src={user_profile && user_profile.photo
                                    ? FOLDER_PROFILE + user_profile.photo
                                    : ava}
                                alt={userName}
                                onError={(e) => { e.target.src = ava; }}
                            />
                            {userLevel > 0 && (
                                <img
                                    src={`/rama/${userLevel}_rama.png`}
                                    alt={`Рамка уровня ${userLevel}`}
                                    className={cl.avatarFrame}
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                />
                            )}
                            <div className={cl.levelBadge}>{userLevel}</div>
                        </div>

                        <h2 className={cl.profileName}>{userName}</h2>
                        <div className={cl.profileSubrole}>
                            {role.icon}
                            <span>{role.label}</span>
                        </div>

                        <div className={cl.statsRow}>
                            <div className={cl.statBox}>
                                <span className={cl.statIcon}>
                                    <IconDiamond width={16} height={16}/>
                                </span>
                                <div>
                                    <div className={cl.statValue}>{diamond || 0}</div>
                                    <div className={cl.statLabel}>алмазов</div>
                                </div>
                            </div>
                            <div className={cl.statBox}>
                                <span className={cl.statIcon}>
                                    <IconStar width={16} height={16}/>
                                </span>
                                <div>
                                    <div className={cl.statValue}>{rating || 0}</div>
                                    <div className={cl.statLabel}>рейтинг</div>
                                </div>
                            </div>
                        </div>

                        <div className={cl.subscriptionRow}>
                            <span className={cl.subscriptionLabel}>Ваша подписка</span>
                            <span className={cl.subscriptionValueEmpty}>
                                <IconAlert width={12} height={12}/>
                                <span>Нет подписки</span>
                            </span>
                        </div>
                    </aside>

                    {/* ===== Правая колонка — инфо ===== */}
                    <section className={cl.infoColumn}>

                        {/* Что такое мой аккаунт */}
                        <div className={cl.infoCard}>
                            <div className={cl.infoCardHead}>
                                <span className={cl.infoCardIcon}>
                                    <IconUser width={18} height={18}/>
                                </span>
                                <h3 className={cl.infoCardTitle}>Что такое мой аккаунт</h3>
                            </div>
                            <p className={cl.infoCardText}>
                                Это ваш <strong>единый аккаунт</strong> во всей экосистеме
                                СтартКласс. Один логин и пароль — и вы заходите в админ-панель
                                своего центра, в школу программирования <strong>Майнкрафтия</strong>
                                и в школу <strong>РобоТехники</strong>. Алмазы, рейтинг и уровень,
                                которые вы видите здесь, — те же, что и в основной школе:
                                их вы получаете за решённые задачи и принятые проекты.
                            </p>
                        </div>

                        {/* Единый аккаунт для трёх сервисов */}
                        <div className={cl.infoCard}>
                            <div className={cl.infoCardHead}>
                                <span className={cl.infoCardIcon}>
                                    <IconExternal width={18} height={18}/>
                                </span>
                                <h3 className={cl.infoCardTitle}>Единый вход на 3 сервиса</h3>
                            </div>
                            <p className={cl.infoCardText}>
                                Один и тот же аккаунт работает на трёх сайтах нашей экосистемы.
                                В каждый можно зайти теми же логином и паролем.
                            </p>
                            <div className={cl.servicesGrid}>
                                {SERVICES.map(s => (
                                    <a
                                        key={s.key}
                                        href={s.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={`${cl.serviceCard} ${cl[`serviceCard_${s.tone}`]}`}
                                    >
                                        <span className={cl.serviceCardIcon}>{s.icon}</span>
                                        <div className={cl.serviceCardBody}>
                                            <div className={cl.serviceCardTitle}>
                                                {s.title}
                                                <IconExternal width={12} height={12}/>
                                            </div>
                                            <div className={cl.serviceCardDesc}>{s.desc}</div>
                                        </div>
                                    </a>
                                ))}
                            </div>
                        </div>

                        {/* Возможности роли */}
                        <div className={cl.infoCard}>
                            <div className={cl.infoCardHead}>
                                <span className={`${cl.infoCardIcon} ${cl[`infoCardIcon_${role.tone}`]}`}>
                                    {role.icon}
                                </span>
                                <h3 className={cl.infoCardTitle}>
                                    Ваша роль: {role.label}
                                </h3>
                            </div>
                            <p className={cl.infoCardText}>{role.summary}</p>
                            <ul className={cl.abilityList}>
                                {role.abilities.map((ability, idx) => (
                                    <li key={idx} className={cl.abilityItem}>
                                        <span className={cl.abilityIcon}>
                                            <IconCheck width={12} height={12}/>
                                        </span>
                                        <span>{ability}</span>
                                    </li>
                                ))}
                            </ul>
                            <div className={cl.roleHint}>
                                <IconAlert width={12} height={12}/>
                                <span>
                                    Роль назначает руководитель центра в разделе
                                    «Преподаватели и админы».
                                </span>
                            </div>
                        </div>

                        {/* Все роли — справочник */}
                        <div className={cl.infoCard}>
                            <div className={cl.infoCardHead}>
                                <span className={cl.infoCardIcon}>
                                    <IconStudents width={18} height={18}/>
                                </span>
                                <h3 className={cl.infoCardTitle}>Какие бывают роли</h3>
                            </div>
                            <div className={cl.rolesGrid}>
                                {Object.entries(ROLE_DESCRIPTIONS).map(([key, r]) => (
                                    <div
                                        key={key}
                                        className={`${cl.roleSlot} ${key === accessRole ? cl.roleSlotActive : ''} ${cl[`roleSlot_${r.tone}`]}`}
                                    >
                                        <div className={cl.roleSlotHead}>
                                            {r.icon}
                                            <span className={cl.roleSlotTitle}>{r.label}</span>
                                            {key === accessRole && (
                                                <span className={cl.roleSlotYou}>вы</span>
                                            )}
                                        </div>
                                        <div className={cl.roleSlotDesc}>{r.summary}</div>
                                    </div>
                                ))}
                            </div>
                        </div>

                    </section>
                </div>
            </div>

            {/* Комментарии (как было) */}
            <Comments />
        </div>
    );
};

export default Me;
